import exchanges from '@/constants/exchanges';
import ReportSubSection from '@components/ReportSubSection';
import ReportNextSteps from '@components/ReportNextSteps';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
  COMPANY_MANAGEMENT_ANCHOR,
} from '@/constants/anchors';
import { kebabCase } from 'lodash';
import useInvestingIdea from '@/pages/CompanyReport/hooks/useInvestingIdea';
import { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { useVisibleSectionsIsPristine } from '../../../VisibleSectionsContextProvider';

const NextSteps = loadable(
  () => import('@/pages/CompanyReport/components/Overview/components/NextSteps')
);

export interface FutureNextStepsProps {
  scorePast: number;
  countryISO: string;
  scoreValue: number;
  scoreHealth: number;
  companyName: string;
  tickerSymbol: string;
  scoreDividend: number;
  exchangeSymbol: string;
  CEOCompensation?: number;
  isDataMeaningless: boolean;
  industryPrimaryName: string;
}

const FutureNextSteps = ({
  scorePast,
  countryISO,
  scoreValue,
  scoreHealth,
  companyName,
  tickerSymbol,
  scoreDividend,
  exchangeSymbol,
  CEOCompensation,
  isDataMeaningless,
  industryPrimaryName,
}: FutureNextStepsProps) => {
  const isPristine = useVisibleSectionsIsPristine();
  const payload = useInvestingIdea(
    'high-growth-potential-companies',
    GridViewOptions.GRID_VIEW_HIGH_GROWTH,
    countryISO.toLowerCase(),
    kebabCase(industryPrimaryName)
  );
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'FutureNextSteps',
  });

  return (
    <Track>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title="Discover growth companies"
        body={
          <ReportNextSteps>
            {isDataMeaningless && (
              <>
                {scoreValue > 2 ? (
                  <li>
                    <span>{`Examine whether ${companyName} is trading at `}</span>
                    <a
                      href={`#${COMPANY_VALUE_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_VALUE_ANCHOR,
                        })
                      }
                    >
                      an attractive price
                    </a>
                    <span>
                      {` based on how much it is expected to earn in the future, and relative to its industry peers and the wider market.`}
                    </span>
                  </li>
                ) : scorePast > 3 ? (
                  <li>
                    <span>Check out </span>
                    <a
                      href={`#${COMPANY_PAST_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_PAST_ANCHOR,
                        })
                      }
                    >
                      {`${companyName}'s track record over time`}
                    </a>
                    <span>
                      {` to understand how much the company has returned to shareholders and its earnings growth rate in the past.`}
                    </span>
                  </li>
                ) : scoreHealth > 3 ? (
                  <li>
                    <span>Examine </span>
                    <a
                      href={`#${COMPANY_HEALTH_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_HEALTH_ANCHOR,
                        })
                      }
                    >
                      {`${companyName}'s financial health`}
                    </a>
                    <span>
                      {` to determine how well-positioned it is against times of financial stress by looking at its level of debt over time and how much cash it has left.`}
                    </span>
                  </li>
                ) : scoreDividend > 3 ? (
                  <li>
                    <span>{`${companyName} is a dividend payer. Have a look at `}</span>
                    <a
                      href={`#${COMPANY_DIVIDEND_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_DIVIDEND_ANCHOR,
                        })
                      }
                    >
                      its current yield, dividend levels over time
                    </a>
                    <span>
                      {` and whether analysts believe the payout ratio will be sustainable moving forward.`}
                    </span>
                  </li>
                ) : typeof CEOCompensation !== 'undefined' &&
                  CEOCompensation !== null ? (
                  <li>
                    <span>Take a look at our analysis of </span>
                    <a
                      href={`#${COMPANY_MANAGEMENT_ANCHOR}`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'other-section',
                          destinationSection: COMPANY_MANAGEMENT_ANCHOR,
                        })
                      }
                    >
                      {`${tickerSymbol}’s management`}
                    </a>
                    <span>
                      {` and see if the CEO’s compensation is within a reasonable range, who is on the board and if insiders have been trading lately.`}
                    </span>
                  </li>
                ) : null}

                {exchangeSymbol === 'OTCPK' ? (
                  <li>
                    <span>
                      {`While we do not consider unaudited financials to be a reliable enough to include in our analysis, you can access them on the `}
                    </span>
                    <a
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                      href={`${
                        exchanges[exchangeSymbol]
                      }/stock/${tickerSymbol.replace('.', '')}/financials`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'external_link',
                          url: exchanges[exchangeSymbol],
                        })
                      }
                    >
                      OTC Markets Website
                    </a>
                    <span>
                      {`. If you are looking for more of a qualitative research into the company, you can access ${companyName}'s filings and announcements `}
                    </span>
                    <a
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                      href={`${
                        exchanges[exchangeSymbol]
                      }/stock/${tickerSymbol.replace('.', '')}/disclosure`}
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'external_link',
                          url: exchanges[exchangeSymbol],
                        })
                      }
                    >
                      here
                    </a>
                    .
                  </li>
                ) : exchanges[exchangeSymbol] ? (
                  <li>
                    <span>{`${companyName} competitive advantages and company strategy can generally be found in its `}</span>
                    <a
                      href={exchanges[exchangeSymbol]}
                      target="_blank"
                      rel="noreferrer noopener nofollow"
                      onClick={() =>
                        trackEvent({
                          action: 'click',
                          modifier: 'external_link',
                          url: exchanges[exchangeSymbol],
                        })
                      }
                    >
                      financial reports
                    </a>
                    <span> archived here.</span>
                  </li>
                ) : null}
              </>
            )}
            <li>
              {typeof payload !== 'undefined' && (
                <NextSteps
                  link={payload.linkToInvestingIdea}
                  industry={industryPrimaryName}
                  section="future"
                />
              )}
            </li>
          </ReportNextSteps>
        }
      />
    </Track>
  );
};

export default FutureNextSteps;
